import { useState } from "react";
import { toast } from "sonner";
import { Youtube, Loader2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";

// Paste a YouTube link → Project Media as audio or video. If YouTube blocks it, offer the file upload.
export const YoutubeMediaDialog = ({ projectId, kind, onClose, onAdded, onUploadInstead }) => {
  const [f, setF] = useState({ url: "", start: "", end: "" });
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState("");
  const go = async () => {
    setBusy(true); setBlocked("");
    try {
      const r = await api.mediaFromYoutube(projectId, { url: f.url.trim(), kind, start: Number(f.start) || 0, end: f.end ? Number(f.end) : null });
      if (r.needs_upload) setBlocked(r.message);
      else { onAdded(r.asset); toast.success(`Added to Project Media — drag onto the timeline to use`); onClose(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't import this link"); }
    setBusy(false);
  };
  const inp = "w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-[#FF3B3B]/60";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md border-white/10 bg-[#0D0D0E] text-white" data-testid="yt-media-dialog">
        <DialogTitle className="flex items-center gap-2"><Youtube size={18} className="text-[#FF3B3B]" /> Add {kind} from YouTube</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">Paste a link. Up to 10 minutes from the start time is imported.</DialogDescription>
        <div className="space-y-2">
          <input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" className={inp} data-testid="yt-media-url" />
          <div className="flex gap-2">
            <input type="number" min={0} value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} placeholder="Start (sec)" className={inp} data-testid="yt-media-start" />
            <input type="number" min={0} value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} placeholder="End (sec, optional)" className={inp} data-testid="yt-media-end" />
          </div>
          <label className="flex items-start gap-2 text-xs leading-snug text-white/70" data-testid="yt-media-licence">
            <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-0.5" data-testid="yt-media-licence-checkbox" />
            I am fully responsible for the licence of this content and for uploading or publishing it online.
          </label>
          {blocked && (
            <div className="rounded-lg border border-[#FFB547]/30 bg-[#FFB547]/10 p-3 text-xs text-[#FFD08A]" data-testid="yt-media-blocked">
              {blocked}
              <button onClick={() => { onClose(); onUploadInstead(); }} data-testid="yt-media-upload-instead" className="mt-2 flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 font-bold text-white hover:bg-white/15"><Upload size={13} /> Upload file instead</button>
            </div>
          )}
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-white/15 px-3 py-2 text-sm">Cancel</button>
          <button onClick={go} disabled={busy || !ok || !f.url.trim()} data-testid="yt-media-import" className="flex items-center gap-1.5 rounded-lg bg-[#FF3B3B] px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
            {busy && <Loader2 size={14} className="animate-spin" />} {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
